import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Image as ImageIcon,
  AlertTriangle,
  Check,
  Search,
  Filter,
  Download,
  ExternalLink,
  RefreshCw,
  Eye,
  ChevronDown,
  FileImage,
  Book,
  Loader2,
  X,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useAuth } from '../../App';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const API = '/api';

// Reason badge colors
const REASON_COLORS = {
  MISSING_COVER: 'bg-red-500/20 text-red-400 border-red-500/30',
  MISSING_THUMB: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  MISSING_CARD: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  NON_PUBLIC_URL: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  BAD_ASPECT: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  PLACEHOLDER_URL: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};

const REASON_LABELS = {
  MISSING_COVER: 'Missing cover',
  MISSING_THUMB: 'Missing thumb',
  MISSING_CARD: 'Missing card',
  NON_PUBLIC_URL: 'Non-public URL',
  BAD_ASPECT: 'Bad aspect ratio',
  PLACEHOLDER_URL: 'Placeholder URL',
};

const AdminCoverAudit = () => {
  useDocumentTitle('Cover Audit - Admin');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  
  // Filters
  const [contentType, setContentType] = useState('all');
  const [status, setStatus] = useState('flagged');
  const [reasonFilter, setReasonFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  
  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Actions
  const [actionLoading, setActionLoading] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNote, setReviewNote] = useState('');

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin && user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  // Fetch audit data
  const fetchAuditData = useCallback(async (reset = false) => {
    if (!user) return;
    
    try {
      if (reset) {
        setLoading(true);
        setItems([]);
        setNextCursor(null);
      }
      
      const params = new URLSearchParams({
        type: contentType,
        status,
        sort: sortBy,
        limit: '50',
      });
      
      if (reasonFilter) params.append('reason', reasonFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (!reset && nextCursor) params.append('cursor', nextCursor);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/audit/covers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (reset) {
        setItems(response.data.items || []);
      } else {
        setItems(prev => [...prev, ...(response.data.items || [])]);
      }
      
      setCounts(response.data.counts || {});
      setHasMore(response.data.has_more || false);
      setNextCursor(response.data.next_cursor || null);
    } catch (error) {
      console.error('Error fetching audit data:', error);
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, [user, contentType, status, reasonFilter, searchTerm, sortBy, nextCursor]);

  // Initial load and filter changes
  useEffect(() => {
    fetchAuditData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, status, reasonFilter, sortBy]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAuditData(true);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle selection
  const toggleSelect = (id, type) => {
    const key = `${type}:${id}`;
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      const allKeys = items.map(item => `${item.type}:${item.id}`);
      setSelectedIds(new Set(allKeys));
    }
    setSelectAll(!selectAll);
  };

  // Get selected items by type
  const getSelectedByType = () => {
    const result = { series: [], novels: [] };
    selectedIds.forEach(key => {
      const [type, id] = key.split(':');
      if (type === 'series') result.series.push(id);
      if (type === 'novels') result.novels.push(id);
    });
    return result;
  };

  // Fix thumbnails action
  const handleFixThumbnails = async () => {
    const selected = getSelectedByType();
    const canFix = items.filter(
      item => selectedIds.has(`${item.type}:${item.id}`) && item.is_cloudinary
    );
    
    if (canFix.length === 0) {
      toast.error('No selected items have Cloudinary covers to fix');
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let totalUpdated = 0;
      
      // Fix series
      if (selected.series.length > 0) {
        const seriesCloudinary = items
          .filter(i => i.type === 'series' && selected.series.includes(i.id) && i.is_cloudinary)
          .map(i => i.id);
        
        if (seriesCloudinary.length > 0) {
          const response = await axios.post(
            `${API}/admin/audit/covers/fix-thumbnails`,
            { type: 'series', ids: seriesCloudinary },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          totalUpdated += response.data.updated || 0;
        }
      }
      
      // Fix novels
      if (selected.novels.length > 0) {
        const novelsCloudinary = items
          .filter(i => i.type === 'novels' && selected.novels.includes(i.id) && i.is_cloudinary)
          .map(i => i.id);
        
        if (novelsCloudinary.length > 0) {
          const response = await axios.post(
            `${API}/admin/audit/covers/fix-thumbnails`,
            { type: 'novels', ids: novelsCloudinary },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          totalUpdated += response.data.updated || 0;
        }
      }
      
      toast.success(`Fixed thumbnails for ${totalUpdated} items`);
      setSelectedIds(new Set());
      setSelectAll(false);
      fetchAuditData(true);
    } catch (error) {
      console.error('Error fixing thumbnails:', error);
      toast.error('Failed to fix thumbnails');
    } finally {
      setActionLoading(false);
    }
  };

  // Mark as reviewed action
  const handleMarkReviewed = async () => {
    const selected = getSelectedByType();
    
    if (selectedIds.size === 0) {
      toast.error('No items selected');
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let totalMarked = 0;
      
      // Mark series
      if (selected.series.length > 0) {
        const response = await axios.post(
          `${API}/admin/audit/covers/mark-reviewed`,
          { type: 'series', ids: selected.series, note: reviewNote },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        totalMarked += response.data.marked || 0;
      }
      
      // Mark novels
      if (selected.novels.length > 0) {
        const response = await axios.post(
          `${API}/admin/audit/covers/mark-reviewed`,
          { type: 'novels', ids: selected.novels, note: reviewNote },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        totalMarked += response.data.marked || 0;
      }
      
      toast.success(`Marked ${totalMarked} items as reviewed`);
      setSelectedIds(new Set());
      setSelectAll(false);
      setShowReviewModal(false);
      setReviewNote('');
      fetchAuditData(true);
    } catch (error) {
      console.error('Error marking as reviewed:', error);
      toast.error('Failed to mark as reviewed');
    } finally {
      setActionLoading(false);
    }
  };

  // Export CSV
  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ type: contentType, status });
      
      window.open(
        `${API}/admin/audit/covers/export.csv?${params}&token=${token}`,
        '_blank'
      );
      toast.success('Export started');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export');
    }
  };

  // Get edit URL for item
  const getEditUrl = (item) => {
    if (item.type === 'series') {
      return `/admin/series/${item.id}`;
    }
    return `/admin/novels/${item.id}`;
  };

  // Render item row
  const renderItem = (item) => {
    const isSelected = selectedIds.has(`${item.type}:${item.id}`);
    
    return (
      <div
        key={`${item.type}:${item.id}`}
        className={`flex items-center gap-4 p-4 bg-zinc-900 rounded-lg border transition-colors ${
          isSelected ? 'border-orange-500/50 bg-orange-500/5' : 'border-zinc-800 hover:border-zinc-700'
        }`}
        data-testid={`audit-item-${item.id}`}
      >
        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => toggleSelect(item.id, item.type)}
          className="data-[state=checked]:bg-orange-500"
        />
        
        {/* Preview Image */}
        <div className="w-16 h-24 bg-zinc-800 rounded overflow-hidden flex-shrink-0">
          {item.thumb_url || item.cover_url ? (
            <img
              src={item.thumb_url || item.cover_url}
              alt={item.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className={`w-full h-full items-center justify-center text-zinc-600 ${item.thumb_url || item.cover_url ? 'hidden' : 'flex'}`}>
            <ImageIcon className="w-6 h-6" />
          </div>
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">
              {item.type === 'series' ? (
                <><FileImage className="w-3 h-3 mr-1" />Series</>
              ) : (
                <><Book className="w-3 h-3 mr-1" />Novel</>
              )}
            </Badge>
            {item.audit_reviewed && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                <CheckCircle2 className="w-3 h-3 mr-1" />Reviewed
              </Badge>
            )}
          </div>
          <h3 className="font-medium text-white truncate">{item.title}</h3>
          <p className="text-sm text-zinc-500 truncate">{item.creator_name || 'Unknown creator'}</p>
        </div>
        
        {/* Reasons */}
        <div className="flex flex-wrap gap-1 max-w-xs">
          {item.reasons.map(reason => (
            <Badge
              key={reason}
              variant="outline"
              className={`text-xs ${REASON_COLORS[reason] || 'bg-zinc-700 text-zinc-300'}`}
            >
              {REASON_LABELS[reason] || reason}
            </Badge>
          ))}
          {item.reasons.length === 0 && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
              <Check className="w-3 h-3 mr-1" />Compliant
            </Badge>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {item.is_cloudinary && (item.reasons.includes('MISSING_THUMB') || item.reasons.includes('MISSING_CARD')) && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
              onClick={async () => {
                setActionLoading(true);
                try {
                  const token = localStorage.getItem('token');
                  await axios.post(
                    `${API}/admin/audit/covers/fix-thumbnails`,
                    { type: item.type, ids: [item.id] },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success('Thumbnails fixed');
                  fetchAuditData(true);
                } catch (error) {
                  toast.error('Failed to fix');
                }
                setActionLoading(false);
              }}
              disabled={actionLoading}
              data-testid={`fix-btn-${item.id}`}
            >
              <RefreshCw className="w-3 h-3 mr-1" />Fix
            </Button>
          )}
          
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={() => window.open(getEditUrl(item), '_blank')}
            data-testid={`edit-btn-${item.id}`}
          >
            <ExternalLink className="w-3 h-3 mr-1" />Edit
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white p-6" data-testid="admin-cover-audit-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
              Cover Audit Tool
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Find and fix non-compliant cover images across series and novels
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="border-zinc-700"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-1" />Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAuditData(true)}
              className="border-zinc-700"
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{counts.flagged || 0}</p>
              <p className="text-xs text-zinc-500">Total Flagged</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{counts.missing_cover || 0}</p>
              <p className="text-xs text-zinc-500">Missing Cover</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-yellow-400">{counts.missing_thumb || 0}</p>
              <p className="text-xs text-zinc-500">Missing Thumb</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-yellow-400">{counts.missing_card || 0}</p>
              <p className="text-xs text-zinc-500">Missing Card</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-orange-400">{counts.non_public || 0}</p>
              <p className="text-xs text-zinc-500">Non-Public</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-purple-400">{counts.bad_aspect || 0}</p>
              <p className="text-xs text-zinc-500">Bad Aspect</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-pink-400">{counts.placeholder || 0}</p>
              <p className="text-xs text-zinc-500">Placeholder</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Content Type */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-zinc-500" />
                <Select value={contentType} onValueChange={setContentType}>
                  <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="series">Series</SelectItem>
                    <SelectItem value="novels">Novels</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status */}
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="flagged">Flagged Only</SelectItem>
                  <SelectItem value="all">All Items</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Reason Filter */}
              <Select value={reasonFilter || "all_reasons"} onValueChange={(v) => setReasonFilter(v === "all_reasons" ? "" : v)}>
                <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700" data-testid="reason-filter">
                  <SelectValue placeholder="All Reasons" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all_reasons">All Reasons</SelectItem>
                  <SelectItem value="MISSING_COVER">Missing Cover</SelectItem>
                  <SelectItem value="MISSING_THUMB">Missing Thumb</SelectItem>
                  <SelectItem value="MISSING_CARD">Missing Card</SelectItem>
                  <SelectItem value="NON_PUBLIC_URL">Non-Public URL</SelectItem>
                  <SelectItem value="BAD_ASPECT">Bad Aspect</SelectItem>
                  <SelectItem value="PLACEHOLDER_URL">Placeholder</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="sort-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                  <SelectItem value="severe">Most Severe</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder="Search by title or creator..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-zinc-800 border-zinc-700"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <Card className="bg-orange-500/10 border-orange-500/30 mb-4">
            <CardContent className="p-3 flex items-center justify-between">
              <span className="text-orange-400 font-medium">
                {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  onClick={handleFixThumbnails}
                  disabled={actionLoading}
                  data-testid="bulk-fix-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />Fix Thumbnails
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                  onClick={() => setShowReviewModal(true)}
                  disabled={actionLoading}
                  data-testid="bulk-review-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />Mark Reviewed
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-zinc-400"
                  onClick={() => {
                    setSelectedIds(new Set());
                    setSelectAll(false);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List Header */}
        <div className="flex items-center gap-4 mb-4 px-4">
          <Checkbox
            checked={selectAll}
            onCheckedChange={toggleSelectAll}
            className="data-[state=checked]:bg-orange-500"
            data-testid="select-all-checkbox"
          />
          <span className="text-sm text-zinc-500">Select all</span>
          <span className="text-sm text-zinc-600 ml-auto">
            {items.length} items shown
          </span>
        </div>

        {/* Items List */}
        <div className="space-y-3">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : items.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-16 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">All Clear!</h3>
                <p className="text-zinc-500">
                  {status === 'flagged' 
                    ? 'No non-compliant covers found with the current filters.'
                    : 'No items match the current filters.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            items.map(renderItem)
          )}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center mt-6">
            <Button
              variant="outline"
              onClick={() => fetchAuditData(false)}
              disabled={loading}
              className="border-zinc-700"
              data-testid="load-more-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ChevronDown className="w-4 h-4 mr-2" />
              )}
              Load More
            </Button>
          </div>
        )}
      </div>

      {/* Mark Reviewed Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-400" />
              Mark as Reviewed
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-400 text-sm mb-4">
              Mark {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} as reviewed.
              They will be excluded from future audits until their covers change.
            </p>
            <Input
              placeholder="Optional note (e.g., 'Acceptable placeholder for now')"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              className="bg-zinc-800 border-zinc-700"
              data-testid="review-note-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReviewModal(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkReviewed}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-review-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Mark Reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoverAudit;
